import _styled from "styled-components";
import { useMemo } from 'react';
import { EnvelopeIcon, PhoneIcon, ClockIcon } from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { formatDateToDayMonthYear } from '@restworld/utils-common';
import { Dropdown, Badge } from '@restworld/ui-ds-base';
import Title from '../title';
import Link from '../link-base';
import { IconButton } from '../icon-button';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function EmployerCollaboratorCard(_ref) {
  var email = _ref.email,
      firstName = _ref.firstName,
      lastName = _ref.lastName,
      lastEdit = _ref.lastEdit,
      phoneNumber = _ref.phoneNumber,
      isAdmin = _ref.isAdmin,
      isInvited = _ref.isInvited,
      onEditCollaborator = _ref.onEditCollaborator,
      onRemoveCollaborator = _ref.onRemoveCollaborator,
      areActionsDisabled = _ref.areActionsDisabled;

  var _useTranslation = useTranslation('collaborators-page'),
      t = _useTranslation.t;

  var menuItemList = useMemo(function () {
    return [{
      label: t('edit_permission_level'),
      onClick: onEditCollaborator,
      Icon: PencilSquareIcon
    }, {
      label: t('remove_collaborator'),
      onClick: onRemoveCollaborator,
      Icon: TrashIcon,
      isCriticalAction: true
    }];
  }, [onEditCollaborator, onRemoveCollaborator, t]);
  return /*#__PURE__*/_jsxs(MainWrapper, {
    children: [/*#__PURE__*/_jsxs(LeftColumnWrapper, {
      children: [/*#__PURE__*/_jsx(Title, {
        fontSize: "base",
        textColor: "neutral-700",
        fontWeight: "semibold",
        children: "".concat(firstName || '', " ").concat(lastName || '')
      }), /*#__PURE__*/_jsxs(EmailPhoneLastEditWrapper, {
        children: [/*#__PURE__*/_jsxs(StyledLink, {
          link: "mailto:".concat(email),
          children: [/*#__PURE__*/_jsx(EnvelopeIcon, {
            className: "h-4 w-4"
          }), /*#__PURE__*/_jsx("span", {
            children: email
          })]
        }), /*#__PURE__*/_jsxs(StyledLink, {
          link: "tel:".concat(phoneNumber),
          children: [/*#__PURE__*/_jsx(PhoneIcon, {
            className: "h-4 w-4"
          }), /*#__PURE__*/_jsx("span", {
            children: phoneNumber
          })]
        }), /*#__PURE__*/_jsx(LastEditDateDisplay, {
          lastEdit: lastEdit
        })]
      }), /*#__PURE__*/_jsx(LastEditDateDisplay, {
        lastEdit: lastEdit,
        forTablet: true
      })]
    }), /*#__PURE__*/_jsxs(RightColumnWrapper, {
      children: [/*#__PURE__*/_jsxs(BadgesWrapper, {
        children: [isInvited && /*#__PURE__*/_jsx(_StyledBadge, {
          variant: "warning",
          children: t('invited')
        }), /*#__PURE__*/_jsx(Badge, {
          variant: isAdmin ? 'restworld' : 'disabled',
          children: t(isAdmin ? 'admin' : 'base')
        })]
      }), /*#__PURE__*/_jsx(Dropdown, {
        items: menuItemList,
        triggerElement: /*#__PURE__*/_jsx(_StyledIconButton, {
          ariaLabel: "view options",
          Icon: EllipsisVerticalIcon,
          size: "sm",
          disabled: areActionsDisabled
        }),
        disabled: areActionsDisabled
      })]
    })]
  });
}
export default EmployerCollaboratorCard;

var LastEditDateDisplay = function LastEditDateDisplay(_ref2) {
  var lastEdit = _ref2.lastEdit,
      forTablet = _ref2.forTablet;

  var _useTranslation2 = useTranslation('collaborators-page'),
      t = _useTranslation2.t;

  return /*#__PURE__*/_jsxs(LastEditWrapper, {
    forTablet: forTablet,
    children: [/*#__PURE__*/_jsx(ClockIcon, {
      className: "h-4 w-4"
    }), /*#__PURE__*/_jsxs("span", {
      children: [t('last_edit'), ": ", formatDateToDayMonthYear(lastEdit)]
    })]
  });
};

var MainWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-collaborator-card__MainWrapper",
  componentId: "sc-1sg2qp-0"
})(["", ""], {
  "display": "flex",
  "flexDirection": "column-reverse",
  "alignItems": "center",
  "justifyContent": "space-between",
  "borderRadius": "1rem",
  "borderWidth": "1px",
  "--tw-border-opacity": "1",
  "borderColor": "rgb(222 222 225 / var(--tw-border-opacity))",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(250 250 250 / var(--tw-bg-opacity))",
  "padding": "1rem",
  "--tw-shadow": "0px 2px 4px 0px rgba(0,0,26,0.05)",
  "--tw-shadow-colored": "0px 2px 4px 0px var(--tw-shadow-color)",
  "boxShadow": "var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)",
  "@media (min-width: 768px)": {
    "flexDirection": "row",
    "gap": "0.5rem",
    "paddingLeft": "1.5rem",
    "paddingRight": "1.5rem",
    "paddingTop": "1.25rem",
    "paddingBottom": "1.25rem"
  }
});

var StyledLink = /*#__PURE__*/_styled(Link).withConfig({
  displayName: "employer-collaborator-card__StyledLink",
  componentId: "sc-1sg2qp-1"
})(["", ""], {
  "display": "flex",
  "alignItems": "center",
  "gap": "0.25rem",
  "fontSize": "0.875rem",
  "lineHeight": "1.5",
  "fontWeight": "500",
  "--tw-text-opacity": "1",
  "color": "rgb(102 102 118 / var(--tw-text-opacity))",
  "@media (min-width: 768px)": {
    "justifyContent": "center"
  }
});

var LeftColumnWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-collaborator-card__LeftColumnWrapper",
  componentId: "sc-1sg2qp-2"
})(["", ""], {
  "width": "100%",
  "@media (min-width: 768px)": {
    "width": "fit-content"
  }
});

var RightColumnWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-collaborator-card__RightColumnWrapper",
  componentId: "sc-1sg2qp-3"
})(["", ""], {
  "marginBottom": "0.5rem",
  "display": "flex",
  "width": "100%",
  "alignItems": "center",
  "justifyContent": "space-between",
  "gap": "0.75rem",
  "@media (min-width: 768px)": {
    "marginBottom": "0px",
    "width": "fit-content",
    "minWidth": "fit-content",
    "justifyContent": "flex-start"
  }
});

var EmailPhoneLastEditWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-collaborator-card__EmailPhoneLastEditWrapper",
  componentId: "sc-1sg2qp-4"
})(["", ""], {
  "marginTop": "0.25rem",
  "display": "flex",
  "flexDirection": "column",
  "gap": "0.25rem",
  "@media (min-width: 768px)": {
    "flexDirection": "row",
    "gap": "1.25rem"
  }
});

var LastEditWrapper = /*#__PURE__*/_styled.span.withConfig({
  displayName: "employer-collaborator-card__LastEditWrapper",
  componentId: "sc-1sg2qp-5"
})(["", " ", ""], {
  "alignItems": "center",
  "gap": "0.25rem",
  "fontSize": "0.875rem",
  "lineHeight": "1.5",
  "fontWeight": "500",
  "--tw-text-opacity": "1",
  "color": "rgb(102 102 118 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "justifyContent": "center"
  }
}, function (_ref3) {
  var forTablet = _ref3.forTablet;
  return forTablet ? {
    "display": "none",
    "@media (min-width: 768px)": {
      "display": "flex"
    },
    "@media (min-width: 1024px)": {
      "display": "none"
    }
  } : {
    "display": "flex",
    "@media (min-width: 768px)": {
      "display": "none"
    },
    "@media (min-width: 1024px)": {
      "display": "flex"
    }
  };
});

var BadgesWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-collaborator-card__BadgesWrapper",
  componentId: "sc-1sg2qp-6"
})(["", ""], {
  "display": "flex",
  "alignItems": "center",
  "gap": "0.75rem"
});

var _StyledBadge = /*#__PURE__*/_styled(Badge).withConfig({
  displayName: "employer-collaborator-card___StyledBadge",
  componentId: "sc-1sg2qp-7"
})({
  "width": "fit-content"
});

var _StyledIconButton = /*#__PURE__*/_styled(IconButton).withConfig({
  displayName: "employer-collaborator-card___StyledIconButton",
  componentId: "sc-1sg2qp-8"
})({
  "--tw-text-opacity": "1",
  "color": "rgb(28 28 51 / var(--tw-text-opacity))"
});